import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../Services/AuthService";
import { Field, FormError } from "../Components/Form/Field";
import { PasswordInput } from "../Components/Form/PasswordInput";
import { SubmitButton } from "../Components/Form/SubmitButton";
import { useSubmit } from "../Hooks/useSubmit";
import Footer from "../Components/Footer";
import "../Styles/Forms.css";

/**
 * Sätter ett nytt lösenord med tokenet ur mailet.
 *
 * Tokenet ligger i query-strängen eftersom det måste kunna klickas. Efter
 * en lyckad återställning skickas användaren till inloggningen — vi loggar
 * medvetet inte in någon på ett token ur ett mail, se api/authRoutes.js.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState<string | undefined>();

  const { submit, pending, fieldError, formError } = useSubmit(async () => {
    await resetPassword({ token, password });
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMismatch(undefined);

    if (password !== confirm) {
      setMismatch("The two passwords do not match.");
      return;
    }

    const result = await submit();
    if (result.ok) {
      navigate("/login", {
        replace: true,
        state: { notice: "Your password has been changed. Sign in with it." },
      });
    }
  };

  if (!token) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <h1>That link is incomplete</h1>
          <p>Open the link from your email again, or request a new one.</p>
          <Link className="form-link" to="/forgot-password">
            Request a new link
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="auth-page">
      <form className="auth-card" onSubmit={handleSubmit} noValidate>
        <h1>Choose a new password</h1>

        <Field
          label="New password"
          error={fieldError("password")}
          hint="At least 8 characters, with a letter and a number."
          required
        >
          {(props) => (
            <PasswordInput
              {...props}
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              disabled={pending}
            />
          )}
        </Field>

        <Field label="Repeat new password" error={mismatch} required>
          {(props) => (
            <PasswordInput
              {...props}
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              disabled={pending}
            />
          )}
        </Field>

        <FormError message={formError ?? fieldError("token")} />

        <SubmitButton pending={pending} pendingLabel="Saving…">
          Save new password
        </SubmitButton>

        <Link className="form-link" to="/forgot-password">
          Request a new link
        </Link>
      </form>
      <Footer />
    </main>
  );
}
